/**
 * Admin bulk-import modal (products or a single retailer's prices).
 *
 * Flow: pick an .xlsx → parsed in the browser → server dry-run preview → the
 * admin reviews an exact diff (field changes, MSRP changes, retailer price
 * bumps, and any errors) → explicit confirm gate → single transactional apply.
 * Nothing is written to the catalog until "Apply" is confirmed.
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  Upload,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  ArrowRight,
  FileSpreadsheet,
} from 'lucide-react';
import {
  MASTER_COLUMNS,
  RETAILER_COLUMNS,
  columnLabel,
  parseWorkbook,
  type SheetColumn,
  type ParsedSheet,
} from '../../services/productSheet';
import {
  runMasterImport,
  runRetailerImport,
  type MasterImportResult,
  type RetailerImportResult,
} from '../../services/adminProductsApi';

interface Props {
  mode: 'master' | 'retailer';
  /** Required for mode='retailer'. */
  retailerId?: number;
  retailerName?: string;
  onClose: () => void;
  /** Called after a successful commit so the parent can refresh. */
  onImported: () => void;
}

const fmt = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};
const money = (n: number | null): string => (n === null ? '(unset)' : `$${n.toFixed(2)}`);

export const AdminImportModal: React.FC<Props> = ({ mode, retailerId, retailerName, onClose, onImported }) => {
  const columns: SheetColumn[] = mode === 'master' ? MASTER_COLUMNS : RETAILER_COLUMNS;
  const fileRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [bumpRetailers, setBumpRetailers] = useState(true);
  const [parsed, setParsed] = useState<ParsedSheet | null>(null);
  const [master, setMaster] = useState<MasterImportResult | null>(null);
  const [retailer, setRetailer] = useState<RetailerImportResult | null>(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const preview = mode === 'master' ? master : retailer;

  const runPreview = useCallback(
    async (p: ParsedSheet, bump: boolean) => {
      setBusy(true);
      setError(null);
      setConfirmChecked(false);
      try {
        if (mode === 'master') {
          setMaster(await runMasterImport({ columns: p.columns, rows: p.rows, options: { bumpMatchingRetailers: bump }, commit: false }));
        } else {
          setRetailer(await runRetailerImport(retailerId!, { columns: p.columns, rows: p.rows, commit: false }));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Preview failed');
        setMaster(null);
        setRetailer(null);
      } finally {
        setBusy(false);
      }
    },
    [mode, retailerId],
  );

  const handleFile = useCallback(
    async (file: File) => {
      setFileName(file.name);
      setError(null);
      setWarnings([]);
      setMaster(null);
      setRetailer(null);
      setParsed(null);
      setDone(null);
      setBusy(true);
      try {
        const p = await parseWorkbook(file, columns);
        if (!p.columns.includes('id')) {
          throw new Error('No "ID" column found — export a fresh file and edit that, keeping the ID column intact.');
        }
        if (p.rows.length === 0) throw new Error('No data rows found in the file.');
        setParsed(p);
        setWarnings(p.unknownHeaders.length ? [`Ignored unrecognized column(s): ${p.unknownHeaders.join(', ')}`] : []);
        await runPreview(p, bumpRetailers);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not read the file');
        setBusy(false);
      }
    },
    [columns, runPreview, bumpRetailers],
  );

  const toggleBump = (next: boolean) => {
    setBumpRetailers(next);
    if (parsed) runPreview(parsed, next);
  };

  const apply = async () => {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'master') {
        const res = await runMasterImport({ columns: parsed.columns, rows: parsed.rows, options: { bumpMatchingRetailers: bumpRetailers }, commit: true });
        setDone(`Applied ${res.summary.productsChanged} product change(s), ${res.summary.priceChanges} MSRP change(s), and ${res.summary.retailerBumps} retailer price bump(s).`);
      } else {
        const res = await runRetailerImport(retailerId!, { columns: parsed.columns, rows: parsed.rows, commit: true });
        setDone(`Applied ${res.summary.priceChanges} price change(s) across ${res.summary.productsChanged} product(s).`);
      }
      onImported();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const summary = preview?.summary;
  const nothingToDo = !!summary && summary.productsChanged === 0 && (mode === 'retailer' || (summary as MasterImportResult['summary']).priceChanges === 0);

  return (
    <div className="modal d-block fade show" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
              <FileSpreadsheet size={18} />
              {mode === 'master' ? 'Import Products (.xlsx)' : `Import Prices — ${retailerName ?? 'Retailer'}`}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" disabled={busy} />
          </div>

          <div className="modal-body">
            {done ? (
              <div className="text-center py-4">
                <CheckCircle2 size={40} className="text-success mb-2" />
                <h6 className="fw-bold">Import complete</h6>
                <p className="text-muted small mb-0">{done}</p>
              </div>
            ) : (
              <>
                {/* File picker */}
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="d-none"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) handleFile(f);
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                    onClick={() => fileRef.current?.click()}
                    disabled={busy}
                  >
                    <Upload size={14} /> {fileName ? 'Choose a different file' : 'Choose .xlsx file'}
                  </button>
                  {fileName && <span className="small text-muted">{fileName}</span>}
                  {busy && <Loader2 size={16} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} />}
                </div>

                <p className="text-muted small">
                  Rows are matched by <strong>ID</strong>. Products not in the file are left unchanged, and unknown IDs are
                  skipped. Only columns present in the file are written{mode === 'master' ? '; a blank optional cell clears that field, while blank prices/flags are left as-is.' : '. Blank price cells are left unchanged.'}
                </p>

                {error && (
                  <div className="alert alert-danger py-2 px-3 small d-flex align-items-center gap-2">
                    <AlertTriangle size={14} /> {error}
                  </div>
                )}
                {warnings.map((w, i) => (
                  <div key={i} className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
                    <AlertTriangle size={14} /> {w}
                  </div>
                ))}

                {mode === 'master' && parsed && (
                  <div className="form-check form-switch small mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      role="switch"
                      id="bumpToggle"
                      checked={bumpRetailers}
                      onChange={e => toggleBump(e.target.checked)}
                      disabled={busy}
                    />
                    <label className="form-check-label" htmlFor="bumpToggle">
                      Also raise retailers who were selling at the old MSRP up to the new MSRP (recommended)
                    </label>
                  </div>
                )}

                {summary && !busy && (
                  <>
                    {/* Summary chips */}
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <span className="badge text-bg-primary">{summary.productsChanged} product(s) changed</span>
                      {mode === 'master' && (
                        <>
                          <span className="badge text-bg-info">{(summary as MasterImportResult['summary']).priceChanges} MSRP change(s)</span>
                          <span className="badge text-bg-warning text-dark">{(summary as MasterImportResult['summary']).retailerBumps} retailer bump(s)</span>
                        </>
                      )}
                      {mode === 'retailer' && (
                        <span className="badge text-bg-info">{(summary as RetailerImportResult['summary']).priceChanges} price change(s)</span>
                      )}
                      {summary.errorCount > 0 && <span className="badge text-bg-danger">{summary.errorCount} error(s)</span>}
                    </div>

                    {nothingToDo && summary.errorCount === 0 && (
                      <div className="alert alert-secondary py-2 px-3 small mb-3">No changes detected — the file matches the current data.</div>
                    )}

                    {/* Errors */}
                    {preview!.errors.length > 0 && (
                      <div className="mb-3">
                        <h6 className="fw-bold small text-danger">Errors (these rows are skipped)</h6>
                        <div className="table-responsive" style={{ maxHeight: 180 }}>
                          <table className="table table-sm table-bordered small mb-0">
                            <thead className="table-light"><tr><th>Row</th><th>SKU</th><th>Problem</th></tr></thead>
                            <tbody>
                              {preview!.errors.map((e, i) => (
                                <tr key={i}><td>{e.row || '—'}</td><td className="font-monospace">{e.sku ?? '—'}</td><td>{e.message}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Master change diff */}
                    {mode === 'master' && master && master.changes.length > 0 && (
                      <div className="mb-3">
                        <h6 className="fw-bold small">Product changes</h6>
                        <div className="table-responsive" style={{ maxHeight: 280 }}>
                          <table className="table table-sm table-bordered small mb-0 align-middle">
                            <thead className="table-light"><tr><th>SKU</th><th>Field</th><th>Current</th><th></th><th>New</th></tr></thead>
                            <tbody>
                              {master.changes.flatMap(c => {
                                const rows: React.ReactNode[] = [];
                                c.fields.forEach((f, fi) => {
                                  rows.push(
                                    <tr key={`${c.id}-f${fi}`}>
                                      <td className="font-monospace">{fi === 0 ? c.sku : ''}</td>
                                      <td>{columnLabel(MASTER_COLUMNS, f.field)}</td>
                                      <td className="text-muted">{fmt(f.from)}</td>
                                      <td><ArrowRight size={12} /></td>
                                      <td className="fw-semibold">{fmt(f.to)}</td>
                                    </tr>,
                                  );
                                });
                                (['usd', 'cad'] as const).forEach(cur => {
                                  const m = c.msrp[cur];
                                  if (!m) return;
                                  rows.push(
                                    <tr key={`${c.id}-m${cur}`}>
                                      <td className="font-monospace">{c.fields.length === 0 && cur === 'usd' ? c.sku : ''}</td>
                                      <td>MSRP {cur.toUpperCase()}</td>
                                      <td className="text-muted">{money(m.from)}</td>
                                      <td><ArrowRight size={12} /></td>
                                      <td className="fw-semibold">{money(m.to)}</td>
                                    </tr>,
                                  );
                                });
                                return rows;
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Master retailer bumps */}
                    {mode === 'master' && master && master.bumps.length > 0 && (
                      <div className="mb-3">
                        <h6 className="fw-bold small">Retailer price bumps</h6>
                        <div className="table-responsive" style={{ maxHeight: 200 }}>
                          <table className="table table-sm table-bordered small mb-0">
                            <thead className="table-light"><tr><th>Retailer</th><th>SKU</th><th>Cur</th><th>From</th><th></th><th>To</th></tr></thead>
                            <tbody>
                              {master.bumps.map((b, i) => (
                                <tr key={i}>
                                  <td>{b.retailerName}</td>
                                  <td className="font-monospace">{b.sku}</td>
                                  <td>{b.currency}</td>
                                  <td className="text-muted">${b.from.toFixed(2)}</td>
                                  <td><ArrowRight size={12} /></td>
                                  <td className="fw-semibold">${b.to.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Retailer price diff */}
                    {mode === 'retailer' && retailer && retailer.changes.length > 0 && (
                      <div className="mb-3">
                        <h6 className="fw-bold small">Price changes</h6>
                        <div className="table-responsive" style={{ maxHeight: 320 }}>
                          <table className="table table-sm table-bordered small mb-0">
                            <thead className="table-light"><tr><th>SKU</th><th>Name</th><th>Cur</th><th>From</th><th></th><th>To</th><th></th></tr></thead>
                            <tbody>
                              {retailer.changes.flatMap(c =>
                                (['usd', 'cad'] as const).flatMap(cur => {
                                  const p = c.prices[cur];
                                  if (!p) return [];
                                  return [(
                                    <tr key={`${c.id}-${cur}`}>
                                      <td className="font-monospace">{c.sku}</td>
                                      <td>{c.name}</td>
                                      <td>{cur.toUpperCase()}</td>
                                      <td className="text-muted">${p.from.toFixed(2)}</td>
                                      <td><ArrowRight size={12} /></td>
                                      <td className="fw-semibold">${p.to.toFixed(2)}</td>
                                      <td>{p.clearsMarkup && <span className="badge text-bg-warning text-dark" title="This price had a % markup; setting a manual price clears it">clears markup</span>}</td>
                                    </tr>
                                  )];
                                }),
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            {done ? (
              <button type="button" className="btn btn-primary" onClick={onClose}>Close</button>
            ) : (
              <>
                {summary && !busy && !nothingToDo && (
                  <div className="form-check me-auto small">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="confirmApply"
                      checked={confirmChecked}
                      onChange={e => setConfirmChecked(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="confirmApply">
                      I've reviewed the changes above and want to update the live catalog.
                    </label>
                  </div>
                )}
                <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-danger d-inline-flex align-items-center gap-1"
                  onClick={apply}
                  disabled={busy || !preview || nothingToDo || !confirmChecked}
                >
                  {busy ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle2 size={14} />}
                  Apply {summary ? summary.productsChanged : ''} change(s)
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminImportModal;
